import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchAnthropicConfig,
  saveAnthropicKey,
  removeAnthropicKey,
  testAnthropicKey,
} from "../api/integrations";

// Admin-only read: the caller gates on role, and the flag keeps a
// non-admin mount from ever issuing the request.
export function useAnthropicConfig(enabled: boolean) {
  return useQuery({
    queryKey: ["integrations", "anthropic"],
    queryFn: fetchAnthropicConfig,
    staleTime: 60_000,
    enabled,
  });
}

// The key decides whether every AI feature works, so the connections
// aggregate refreshes with this block. The mutation response is not
// cached - a probe verdict must not outlive the click that earned it.
function useAnthropicInvalidation() {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["integrations", "anthropic"] }),
      queryClient.invalidateQueries({ queryKey: ["integrations", "status"] }),
    ]);
  };
}

// One mutation key for every write, so the surface holding the form can
// ask "is a call in flight?" without owning the mutations.
const ANTHROPIC_WRITE = ["integrations", "anthropic"];

export function useSaveAnthropicKey() {
  const invalidate = useAnthropicInvalidation();
  return useMutation({
    mutationKey: ANTHROPIC_WRITE,
    mutationFn: saveAnthropicKey,
    onSuccess: invalidate,
  });
}

export function useRemoveAnthropicKey() {
  const invalidate = useAnthropicInvalidation();
  return useMutation({
    mutationKey: ANTHROPIC_WRITE,
    mutationFn: removeAnthropicKey,
    onSuccess: invalidate,
  });
}

// Read-only probe: nothing to invalidate.
export function useTestAnthropicKey() {
  return useMutation({ mutationKey: ANTHROPIC_WRITE, mutationFn: testAnthropicKey });
}
